import type { ChatScopeFilters } from "../../api";
import { get_process_details } from "../../api";
import type { Process } from "../../api/types";
import { parseUtcDate } from "../../utils";
import { format } from "date-fns";
import {
  buildInstanceScopeIndex,
  instanceScopeFromIds,
  type InstanceScope,
} from "./processScopeModel";
import type { ProcessComparison } from "./processComparison";

/** Only selection identifiers travel in navigation state, never entire process graphs. */
export type ProcessAction =
  | { kind: "session"; filters: ChatScopeFilters }
  | { kind: "processes"; filters: ChatScopeFilters; secondSlug: string };

export function actionRuns(process: Process, filters: ChatScopeFilters) {
  return process.instances.filter(
    ({ id, source }) =>
      (!filters.userIds || filters.userIds.includes(source.userId)) &&
      (!filters.instanceIds || filters.instanceIds.includes(id)) &&
      (!filters.recordingIds || filters.recordingIds.includes(source.recordingId)) &&
      (!filters.from || parseUtcDate(source.traceEnd) >= new Date(filters.from)) &&
      (!filters.to || parseUtcDate(source.traceStart) < new Date(filters.to)),
  );
}

export type ResolvedProcessAction = {
  ids: string[];
  scope: InstanceScope;
  comparison?: ProcessComparison;
};

function selectionScope(process: Process, filters: ChatScopeFilters, ids: string[]): InstanceScope {
  return {
    ...instanceScopeFromIds(buildInstanceScopeIndex(process.instances), ids),
    userIds: filters.userIds ?? [],
    noUsers: filters.userIds?.length === 0,
    from:
      filters.from && new Date(filters.from).getTime() !== 0
        ? format(new Date(filters.from), "yyyy-MM-dd")
        : null,
    to: filters.to ? format(new Date(new Date(filters.to).getTime() - 1), "yyyy-MM-dd") : null,
  };
}

export async function resolveProcessAction(
  process: Process,
  action: ProcessAction,
): Promise<ResolvedProcessAction> {
  const runs = actionRuns(process, action.filters);
  if (action.kind === "session") {
    const ids = runs.map(run => run.id);
    return { ids, scope: selectionScope(process, action.filters, ids) };
  }
  const other = await get_process_details(action.secondSlug);
  const a = runs;
  const b = actionRuns(other, action.filters);
  return {
    ids: a.map(run => run.id),
    scope: selectionScope(
      process,
      action.filters,
      runs.map(run => run.id),
    ),
    comparison: {
      mode: action.kind,
      useAll: false,
      a: { process, runs: a, label: process.name },
      b: {
        process: other,
        runs: b,
        label: other.name,
      },
    },
  };
}
